import { compressorTypes, DeviceType } from '@/constants/deviceTypeEnum';
import { DeviceKind } from '@/constants/enums';
import type { Device } from '@/store/Station/constants';
import DevicesStore from '@/store/Devices/DevicesStore';
import { SCC_BLACKLIST_TYPES } from '../config/scc-blacklist-types';

export type DeviceFilterType = 'all' | DeviceType;

const ALL_DEVICE_TYPES = Object.values(DeviceType) as DeviceType[];

const BLACKLIST_SET = new Set(SCC_BLACKLIST_TYPES);

const getAvailableDeviceTypes = (): DeviceType[] => {
  return ALL_DEVICE_TYPES.filter(deviceType => !BLACKLIST_SET.has(deviceType));
};

export const getAvailableDeviceTypesForStation = (devices: Device[], devicesStore: DevicesStore): DeviceType[] => {
  const types = new Set<DeviceType>();

  for (const device of devices) {
    if (BLACKLIST_SET.has(device.deviceType)) continue;

    const deviceMetadata = devicesStore.getDeviceById(device.deviceId);
    const deviceModel = deviceMetadata?.deviceModel as { deviceKind?: DeviceKind } | undefined;
    const isNotCompressorAndDisconnected =
      !compressorTypes.includes(device.deviceType) && deviceModel?.deviceKind === DeviceKind.Disconnected;

    if (isNotCompressorAndDisconnected) continue;

    types.add(device.deviceType);
  }

  return Array.from(types);
};

export const getDeviceTypesForFilter = (filter: DeviceFilterType): DeviceType[] => {
  if (filter === 'all') {
    return getAvailableDeviceTypes();
  }

  return BLACKLIST_SET.has(filter) ? [] : [filter];
};

export const getDeviceFilterOptions = (
  devices: Device[],
  devicesStore: DevicesStore,
): Array<{ value: DeviceFilterType; label: string }> => {
  const options: Array<{ value: DeviceFilterType; label: string }> = [{ value: 'all', label: 'All Assets Types' }];

  const availableDeviceTypes = getAvailableDeviceTypesForStation(devices, devicesStore)
    .slice()
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  availableDeviceTypes.forEach(deviceType => {
    options.push({ value: deviceType, label: deviceType });
  });

  return options;
};

export const filterAndSortDevicesForControlCenter = (
  devices: Device[],
  filter: DeviceFilterType,
  devicesStore: DevicesStore,
): Device[] => {
  const allowedTypes = new Set(getDeviceTypesForFilter(filter));

  return devices
    .filter(device => {
      const isNotBlacklisted = !BLACKLIST_SET.has(device.deviceType);
      const matchesFilter = allowedTypes.has(device.deviceType);

      const deviceMetadata = devicesStore.getDeviceById(device.deviceId);
      const deviceModel = deviceMetadata?.deviceModel as { deviceKind?: DeviceKind } | undefined;
      const isNotCompressorAndDisconnected =
        !compressorTypes.includes(device.deviceType) && deviceModel?.deviceKind === DeviceKind.Disconnected;

      return isNotBlacklisted && matchesFilter && !isNotCompressorAndDisconnected;
    })
    .slice()
    .sort((c1, c2) => (c1.elementIndex || 0) - (c2.elementIndex || 0));
};
